/**
 * TA 此刻在家还是在外面：主屏跟着 TA 走（用户 2026-09-27 定：「按旅行青蛙那样，在家主屏是小屋、出门是地图」）。
 * - 主屏入口跟着变：在家是“小屋”（去 /home），在外面是“地图”（去 /map）；“/” 入口、通讯器和回忆的返回去处都按这个走。
 * - 口径（I 定）：先看 life.activity.kind——away 是在外面，sleeping / awake / arriving 是在家；
 *   没有 life（旧后端）或 kind 是 unknown，退回看 presence。前端不按本机时钟推。
 * - 两样都说不清（还没入住、presence unknown、快照还没读到）：按“不知道”处理，入口去地图（地图会如实说 TA 的状态）。
 * - 演示模式：没有家庭列表，按演示家园快照的 presence 认（和小窝页读的是同一份缓存）。
 * 放在 app 壳：“/” 入口和各页的返回去处都用它（app 壳不引 features，features 可以引 app）。
 * 底栏用户 10-03 撤了；没有地方渲染的 MainTabBar 和全屏页的 PreviewTabBar 按 I 10-14 定删掉（总账 N-06）。
 */
import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router";
import type { HomeLife, HomeSnapshot, PetPresence } from "@/shared/contracts";
import { env } from "@/shared/config/env";
import { LoadingState, Page, type IconName } from "@/shared/ui";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";

export type Whereabouts = "home" | "out" | "unknown";

const OUT_PRESENCE: ReadonlySet<PetPresence> = new Set<PetPresence>(["in_transit", "at_destination", "visiting", "returning"]);

export function whereaboutsOf(pet: { presence?: PetPresence | null; life?: HomeLife | null } | null | undefined): Whereabouts {
  const kind = pet?.life?.activity.kind;
  if (kind === "away") return "out";
  if (kind === "sleeping" || kind === "awake" || kind === "arriving") return "home";
  const presence = pet?.presence;
  if (presence === "at_home") return "home";
  if (presence && OUT_PRESENCE.has(presence)) return "out";
  return "unknown";
}

export interface PrimaryTab {
  to: "/home" | "/map";
  label: "小屋" | "地图";
  icon: IconName;
}

export const HOME_TAB: PrimaryTab = { to: "/home", label: "小屋", icon: "home" };
export const MAP_TAB: PrimaryTab = { to: "/map", label: "地图", icon: "pin" };

export function primaryTabFor(where: Whereabouts): PrimaryTab {
  return where === "home" ? HOME_TAB : MAP_TAB;
}

/** 当前宠物在不在家；ready 为 false 表示还没读到（演示快照在读），调用方先别跳转。 */
export function useWhereabouts(): { where: Whereabouts; ready: boolean } {
  const household = useOptionalCurrentHousehold();
  const services = useServices();
  const fixture = env.dataMode === "fixture";
  // 演示：和 useActiveHome 同一个查询键，读的是同一份缓存。live 下不碰 world 服务（到取数时才访问，测试里的服务垫片也不会被惊动）
  const petId = household?.pet?.pet_id ?? null; // 演示下家庭上下文没有宠物，和 useActiveHome 一样传当前宠物（演示时是 null）
  const demo = useQuery<HomeSnapshot>({ queryKey: queryKeys.home, queryFn: ({ signal }) => services.world.home(petId, signal), enabled: fixture });
  if (fixture) return { where: demo.data ? whereaboutsOf(demo.data) : "unknown", ready: !demo.isPending };
  return { where: whereaboutsOf(household?.pet), ready: true };
}

export function usePrimaryTab(): PrimaryTab {
  return primaryTabFor(useWhereabouts().where);
}

/** “/” 入口：TA 在家进小屋，在外面或说不清进地图；演示快照还没读到时先等一下，不先跳错地方。 */
export function EntryRedirect() {
  const { where, ready } = useWhereabouts();
  if (!ready) {
    return (
      <Page>
        <LoadingState lines={2} label="正在看 TA 在不在家…" />
      </Page>
    );
  }
  return <Navigate to={primaryTabFor(where).to} replace />;
}
